/**
 * Utility functions for file operations
 */

/**
 * Format bytes to human readable format
 * @param bytes - Number of bytes
 * @param decimals - Number of decimal places
 * @returns Formatted string
 */
export function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * Get file type category from extension
 * @param filename - The filename
 * @returns File type category
 */
export function getFileTypeCategory(filename: string): 'image' | 'video' | 'audio' | 'document' | 'code' | 'archive' | 'other' {
  const extension = filename.split('.').pop()?.toLowerCase();
  
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp', 'ico'];
  const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'webm', 'mkv', 'flv'];
  const audioExtensions = ['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'];
  const documentExtensions = ['pdf', 'doc', 'docx', 'txt', 'rtf', 'odt', 'xls', 'xlsx', 'ppt', 'pptx'];
  const codeExtensions = ['js', 'ts', 'jsx', 'tsx', 'py', 'java', 'cpp', 'c', 'html', 'css', 'scss', 'php', 'rb', 'go', 'rs', 'swift'];
  const archiveExtensions = ['zip', 'rar', '7z', 'tar', 'gz', 'bz2'];

  if (extension) {
    if (imageExtensions.includes(extension)) return 'image';
    if (videoExtensions.includes(extension)) return 'video';
    if (audioExtensions.includes(extension)) return 'audio';
    if (documentExtensions.includes(extension)) return 'document';
    if (codeExtensions.includes(extension)) return 'code';
    if (archiveExtensions.includes(extension)) return 'archive';
  }

  return 'other';
}

/**
 * Check if file type is allowed for upload
 * @param file - The file to check
 * @returns Whether the file type is allowed
 */
export function isFileTypeAllowed(file: File): boolean {
  // Add your file type restrictions here
  const maxSize = 50 * 1024 * 1024; // 50MB
  const disallowedExtensions = ['exe', 'bat', 'cmd', 'scr', 'pif', 'com'];
  
  if (file.size > maxSize) {
    return false;
  }

  const extension = file.name.split('.').pop()?.toLowerCase();
  if (extension && disallowedExtensions.includes(extension)) {
    return false;
  }

  return true;
}

/**
 * Generate safe filename for storage
 * @param filename - Original filename
 * @returns Safe filename
 */
export function generateSafeFilename(filename: string): string {
  // Replace spaces with underscores and remove special characters
  const baseName = filename.replace(/[^a-zA-Z0-9.\-_]/g, '_');
  
  // Add timestamp to make it unique
  const timestamp = Date.now();
  const extension = baseName.split('.').pop();
  const nameWithoutExtension = baseName.substring(0, baseName.lastIndexOf('.'));
  
  return `${timestamp}_${nameWithoutExtension}.${extension}`;
}

/**
 * Check if a file is an image that can be previewed
 * @param filename - The filename
 * @returns Whether the file can be previewed as an image
 */
export function isPreviewableImage(filename: string): boolean {
  const extension = filename.split('.').pop()?.toLowerCase();
  const previewableExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
  
  return extension ? previewableExtensions.includes(extension) : false;
}

/**
 * Validate folder name
 * @param name - Folder name to validate
 * @returns Validation result
 */
export function validateFolderName(name: string): { valid: boolean; error?: string } {
  if (!name || name.trim().length === 0) {
    return { valid: false, error: 'Folder name cannot be empty' };
  }

  if (name.length > 100) {
    return { valid: false, error: 'Folder name must be less than 100 characters' };
  }

  if (!/^[a-zA-Z0-9\s\-_\.]+$/.test(name)) {
    return { valid: false, error: 'Folder name contains invalid characters' };
  }

  const reservedNames = ['con', 'prn', 'aux', 'nul', 'com1', 'com2', 'com3', 'com4', 'com5', 'com6', 'com7', 'com8', 'com9', 'lpt1', 'lpt2', 'lpt3', 'lpt4', 'lpt5', 'lpt6', 'lpt7', 'lpt8', 'lpt9'];
  if (reservedNames.includes(name.toLowerCase())) {
    return { valid: false, error: 'This folder name is reserved and cannot be used' };
  }

  return { valid: true };
}
